import z from "zod";

// Response from POST /oauth-requests
export const OAuthRequestResponseSchema = z.object({
  authURL: z.string(), // base64 encoded
});

// Google Workspace org-level config. The Service Account key is write-only:
// the backend never returns it, so it is never present on this object.
export const GoogleWorkspaceConfigSchema = z.object({
  id: z.string(),
  googleDomain: z.string(),
  saClientId: z.string(),
  saEmail: z.string(),
  gcpProjectId: z.string(),
  // The Workspace admin the SA impersonates for domain-wide Shared Drive
  // listing. Empty when unset, leaving only shared-with-SA content browsable.
  subjectEmail: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
  // Set when the SA can no longer impersonate the subject or the key was
  // revoked; Drive ingestion is paused until it clears.
  authBrokenAt: z.string().nullish(),
  enabled: z.boolean(),
  projectIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  // The delegation set the Workspace admin must authorize.
  requiredScopes: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  // Set when a runtime call recently hit a scope-not-authorized 403, and cleared
  // once a verify finds every scope granted. scopeGapScopes names the scopes
  // missing on that occurrence.
  scopeGapAt: z.string().nullish(),
  scopeGapScopes: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  // Pre-checks calendar auto-scheduling for org members who have not answered
  // for themselves; it never schedules on their behalf.
  calendarDefaultEnabled: z.boolean(),
});
export type GoogleWorkspaceConfig = z.infer<typeof GoogleWorkspaceConfigSchema>;

// Response from GET /integrations/google
export const GoogleWorkspaceConfigsResponseSchema = z.array(GoogleWorkspaceConfigSchema);

// Response from GET /integrations/google/scopes
export const GoogleScopesResponseSchema = z.object({
  scopes: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
});

// "unknown" means the probe was inconclusive (a transient failure), never that
// the scope is missing.
export const GoogleScopeCheckSchema = z.object({
  scope: z.string(),
  status: z.enum(["granted", "missing", "unknown"]),
});
export type GoogleScopeCheck = z.infer<typeof GoogleScopeCheckSchema>;

// Response from POST /integrations/google/configs/{integrationId}/scopes/verify.
// "unverifiable" means no identity could run the probe, so scopes comes back empty.
export const GoogleVerifyScopesResponseSchema = z.object({
  status: z.enum(["verified", "unverifiable"]),
  scopes: z
    .array(GoogleScopeCheckSchema)
    .nullish()
    .transform((v) => v ?? []),
});
export type GoogleVerifyScopesResponse = z.infer<typeof GoogleVerifyScopesResponseSchema>;

// all_shared ingests every item shared with the SA, from anyone, now or later;
// selected ingests only the picked Shared Drives / folders and their subtrees.
export const GoogleSyncModeSchema = z.enum(["all_shared", "selected"]);
export type GoogleSyncMode = z.infer<typeof GoogleSyncModeSchema>;

// One node of the Drive tree; the response is flat and the client rebuilds the
// nesting from parentId.
export const GoogleDiscoverNodeSchema = z.object({
  id: z.string(),
  type: z.enum(["drive", "folder", "file"]),
  name: z.string(),
  parentId: z.string().optional(),
  driveId: z.string().optional(),
});
export type GoogleDiscoverNode = z.infer<typeof GoogleDiscoverNodeSchema>;

// One resolved saved root. found is the only authority on whether the SA can
// still see it; absence from the node list never implies it. An unreadable root
// resolves to id and found alone, so type and name have to survive being absent
// or the whole picker fails to parse on the one response that needs it most.
export const GoogleResolvedNodeSchema = z.object({
  id: z.string(),
  type: z.enum(["drive", "folder", "file"]).default("folder"),
  name: z.string().default(""),
  driveId: z.string().optional(),
  found: z.boolean(),
});

// Response from GET /integrations/google/configs/{id}/discover. Without a
// parentId the nodes are the top level (Shared Drives + shared-with-SA roots);
// with one they are that node's immediate child folders.
export const GoogleDiscoverResponseSchema = z.object({
  nodes: z
    .array(GoogleDiscoverNodeSchema)
    .nullable()
    .transform((v) => v ?? []),
  resolved: z
    .array(GoogleResolvedNodeSchema)
    .nullish()
    .transform((v) => v ?? []),
  // The backend caps how many children one level returns, so nodes can be a
  // prefix of the real level. Absent on backends predating the cap signal, which
  // read as complete.
  hasMore: z
    .boolean()
    .nullish()
    .transform((v) => v ?? false),
});
export type GoogleDiscoverResponse = z.infer<typeof GoogleDiscoverResponseSchema>;

// Path + query params for GET /integrations/google/configs/{id}/discover.
// driveId scopes parentId to a Shared Drive; resolveIds asks the top-level call
// to also resolve saved roots.
export const GoogleWorkspaceDiscoverParamsSchema = z.object({
  integrationId: z.string(),
  parentId: z.string().optional(),
  driveId: z.string().optional(),
  resolveIds: z.array(z.string()).optional(),
});

// Response from GET /integrations/google/configs/{id}/projects/{projectId}
export const GoogleDeploymentResponseSchema = z.object({
  selectedRootIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  syncMode: GoogleSyncModeSchema,
  lastSyncedAt: z.string().nullish(),
});
export type GoogleDeployment = z.infer<typeof GoogleDeploymentResponseSchema>;

// Response from GET /integrations/google/configs/{id}/discover/count. One page
// only, so count tops out at 100 and hasMore means the real total is higher.
export const GoogleFolderCountResponseSchema = z.object({
  count: z.number(),
  hasMore: z.boolean(),
});
export type GoogleFolderCount = z.infer<typeof GoogleFolderCountResponseSchema>;

// Path + query params for GET /integrations/google/configs/{id}/discover/count.
// driveId scopes the count to a Shared Drive; absent for the SA's own corpus.
export const GoogleWorkspaceFolderCountParamsSchema = z.object({
  integrationId: z.string(),
  folderId: z.string(),
  driveId: z.string().optional(),
});

// PUT /integrations/google/configs/{id}/subject body. An empty value clears the
// subject and turns Shared-Drive browsing off.
export const UpdateGoogleWorkspaceSubjectInputSchema = z.object({
  subjectEmail: z.string(),
});

// The caller's own calendar auto-scheduling answer. `explicit` is false until
// they save one, in which case `enabled` echoes the org default.
export const GoogleCalendarPreferenceSchema = z.object({
  enabled: z.boolean(),
  explicit: z.boolean(),
  orgDefault: z.boolean(),
});
export type GoogleCalendarPreference = z.infer<typeof GoogleCalendarPreferenceSchema>;

// PUT /integrations/google/calendar/me body. Each meeting is filed on its own,
// so there is no project to pick.
export const UpdateGoogleCalendarPreferenceInputSchema = z.object({
  enabled: z.boolean(),
});
export type UpdateGoogleCalendarPreferenceRequest = z.infer<typeof UpdateGoogleCalendarPreferenceInputSchema>;

// PUT /integrations/google/configs/{id}/calendar body and response.
export const GoogleCalendarDefaultSchema = z.object({
  defaultEnabled: z.boolean(),
});
export type GoogleCalendarDefault = z.infer<typeof GoogleCalendarDefaultSchema>;

// PUT /integrations/google/configs/{id}/projects/{projectId}/sync-mode body.
// allowPrune must be true to commit a narrowing change; the backend deletes the
// now-out-of-scope assets from the project.
export const UpdateGoogleWorkspaceSyncModeInputSchema = z.object({
  syncMode: GoogleSyncModeSchema,
  selectedRootIds: z.array(z.string()),
  allowPrune: z.boolean(),
});

// Slack org-level integration config. The bot token and client secret are
// write-only: the backend never returns them, so they are never present here.
export const SlackIntegrationConfigSchema = z.object({
  id: z.string(),
  slackTeamId: z.string(),
  teamName: z.string(),
  // Empty when the integration predates the field or the name was left blank;
  // both render as the manifest's default app name.
  appName: z.string().default(""),
  installType: z.string(),
  botUserId: z.string(),
  enabled: z.boolean(),
  // Absent from a control plane that predates the inbound bot, so each defaults
  // rather than failing the whole list. Empty URLs also mean the deployment has
  // no hooks host. Slack won't report a BYO app's event configuration, so a null
  // lastEventAt is the only evidence the request URL was never pasted in.
  signingSecretConfigured: z.boolean().default(false),
  needsUpdate: z.boolean().default(false),
  lastEventAt: z.string().nullable().default(null),
  eventsRequestUrl: z.string().default(""),
  interactivityRequestUrl: z.string().default(""),
  projectIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
});
export type SlackIntegrationConfig = z.infer<typeof SlackIntegrationConfigSchema>;

// Response from GET /integrations/slack
export const SlackIntegrationConfigsResponseSchema = z.array(SlackIntegrationConfigSchema);

// One Slack workspace the user can link their own account to. projectId is a
// project the user reaches that the workspace is deployed to.
export const SlackAccountSchema = z.object({
  integrationId: z.string(),
  teamName: z.string(),
  appName: z.string(),
  organizationId: z.string(),
  projectId: z.string(),
  connected: z.boolean(),
  // Only present on a connected row, and only when the backend could resolve
  // the identity behind the grant. The handle carries no leading @.
  slackUserName: z.string().optional(),
  slackEmail: z.string().optional(),
});
export type SlackAccount = z.infer<typeof SlackAccountSchema>;

// Response from GET /users/integrations/slack
export const SlackAccountsResponseSchema = z.object({
  accounts: z
    .array(SlackAccountSchema)
    .nullable()
    .transform((v) => v ?? []),
});

// Nothing about a channel is persisted until it is bound, so this is Slack's
// own shape and keeps its snake_case field.
export const SlackChannelSchema = z.object({
  id: z.string(),
  name: z.string(),
  is_private: z.boolean(),
});
export type SlackChannel = z.infer<typeof SlackChannelSchema>;

// Response from GET /integrations/slack/configs/{integrationId}/slack-channels
export const SlackChannelsResponseSchema = z.object({
  channels: z
    .array(SlackChannelSchema)
    .nullable()
    .transform((v) => v ?? []),
});

// The name is the label captured at bind time: empty on older bindings, and
// stale if the channel has been renamed in Slack since. Fall back to the id.
export const SlackChannelBindingSchema = z.object({
  slackChannelId: z.string(),
  slackChannelName: z.string(),
  projectId: z.string(),
});
export type SlackChannelBinding = z.infer<typeof SlackChannelBindingSchema>;

// Response from GET /integrations/slack/configs/{integrationId}/channels
export const SlackChannelBindingsResponseSchema = z
  .array(SlackChannelBindingSchema)
  .nullable()
  .transform((v) => v ?? []);

// An action that posts each run into the channel. A disabled one was paused by
// the system rather than by a person. createdByName is empty when the creator
// has only an email.
export const SlackChannelActionSchema = z.object({
  id: z.string(),
  name: z.string(),
  schedule: z.string(),
  status: z.enum(["active", "paused", "disabled"]),
  createdByName: z.string(),
  canUpdate: z.boolean(),
  canDelete: z.boolean(),
});
export type SlackChannelAction = z.infer<typeof SlackChannelActionSchema>;

// canEdit is true for owners of the bound project; everyone else who reaches the
// project gets it read-only.
export const SlackChannelSettingsSchema = z.object({
  slackChannelId: z.string(),
  slackChannelName: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  instructions: z.string(),
  autoRespond: z.boolean(),
  canEdit: z.boolean(),
  actions: z
    .array(SlackChannelActionSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type SlackChannelSettings = z.infer<typeof SlackChannelSettingsSchema>;

export const UpdateSlackChannelSettingsInputSchema = z.object({
  instructions: z.string().optional(),
  autoRespond: z.boolean().optional(),
});

export const SetSlackChannelActionEnabledInputSchema = z.object({
  enabled: z.boolean(),
});

// A channel bound to the focused project, which that project's actions may post
// to. The name is the label captured at bind time, so it is empty on older
// bindings.
export const SlackProjectChannelSchema = z.object({
  integrationId: z.string(),
  channelId: z.string(),
  channelName: z.string(),
});
export type SlackProjectChannel = z.infer<typeof SlackProjectChannelSchema>;

// Response from GET /integrations/slack/channels, ordered by name
export const SlackProjectChannelsResponseSchema = z
  .array(SlackProjectChannelSchema)
  .nullable()
  .transform((v) => v ?? []);

// Response from GET /integrations/slack/manifest. The manifest is
// pre-serialized JSON meant for copy-paste into Slack's create-app page.
export const SlackManifestResponseSchema = z.object({
  manifest: z.string(),
});
export type SlackManifestResponse = z.infer<typeof SlackManifestResponseSchema>;

// Query params for GET /integrations/slack/manifest. The name is what Slack
// shows in the mention autocomplete; empty renders the default.
export const SlackManifestParamsSchema = z.object({
  appName: z.string(),
});

// A Slack/Google/Notion/Linear/GitHub entity the user can @-mention in the
// prompt bar. GitHub discussions are not mentionable and never arrive here.
export const MentionableItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  sublabel: z.string().optional(),
  type: z.enum([
    "slack_channel",
    "slack_user",
    "gmail_contact",
    "gmail_label",
    "notion_page",
    "notion_user",
    "linear_issue",
    "linear_user",
    "gdrive_file",
    "gdrive_folder",
    "github_issue",
    "github_pull_request",
  ]),
});
export type MentionableItem = z.infer<typeof MentionableItemSchema>;

// Response from GET /integrations/{slack,google,notion,linear,github}/mentionables/* (capped at 25 items)
export const MentionablesResponseSchema = z.object({
  items: z
    .array(MentionableItemSchema)
    .nullable()
    .transform((v) => v ?? []),
});

export const MentionablesQueryParamsSchema = z.object({
  q: z.string().optional(),
});

export const EntityProviderSchema = z.enum(["slack", "google", "notion", "linear", "github"]);
export type EntityProvider = z.infer<typeof EntityProviderSchema>;

export const EntityKindSchema = z.enum([
  "slack_channel",
  "slack_user",
  "gmail_contact",
  "gmail_label",
  "notion_page",
  "notion_user",
  "linear_issue",
  "linear_user",
  "gdrive_file",
  "gdrive_folder",
  // A pull request is its own kind rather than an issue carrying a flag: GitHub's
  // REST surface treats every pull request as an issue, so without the split the
  // typeahead would label them all "issue".
  "github_issue",
  "github_pull_request",
  "github_discussion",
  "github_user",
]);
export type EntityKind = z.infer<typeof EntityKindSchema>;

// A node in the project's integration entity graph. The backend already
// filters owner-scoped rows (Google contacts/labels) to the caller.
export const EntitySchema = z.object({
  id: z.string(),
  provider: EntityProviderSchema,
  kind: EntityKindSchema,
  externalId: z.string(),
  displayName: z.string(),
  lastSeenAt: z.string(),
  seenCount: z.number(),
  metadata: z
    .record(z.string(), z.unknown())
    .nullish()
    .transform((v) => v ?? {}),
});
export type Entity = z.infer<typeof EntitySchema>;

// Response from GET /entities
export const EntitiesResponseSchema = z.object({
  items: z
    .array(EntitySchema)
    .nullable()
    .transform((v) => v ?? []),
});

// Edge kinds the frontend knows how to label. The backend grows this
// vocabulary ahead of the UI, so edgeKind parses as a plain string — an
// unknown kind must not fail the whole detail response.
export const KNOWN_ENTITY_EDGE_KINDS = [
  "active_in",
  "same_person_as",
  "co_participant",
  "mentioned_with",
  "replied_to",
  "attended_with",
  "communicates_with",
] as const;
export type EntityEdgeKind = (typeof KNOWN_ENTITY_EDGE_KINDS)[number];

// A typed edge from the requested entity to a connected one. direction is set
// only for directed kinds and says whether the requested entity is the edge's
// source (outgoing) or its target (incoming).
export const EntityConnectionSchema = z.object({
  edgeKind: z.string(),
  direction: z.enum(["outgoing", "incoming"]).optional(),
  observedCount: z.number(),
  lastSeenAt: z.string(),
  entity: z.object({
    id: z.string(),
    provider: EntityProviderSchema,
    kind: EntityKindSchema,
    displayName: z.string(),
  }),
});
export type EntityConnection = z.infer<typeof EntityConnectionSchema>;

// Response from GET /entities/{id}
export const EntityDetailResponseSchema = z.object({
  entity: EntitySchema,
  connections: z
    .array(EntityConnectionSchema)
    .nullable()
    .transform((v) => v ?? []),
});

// Query params for GET /entities
export const EntitiesQueryParamsSchema = z.object({
  kind: EntityKindSchema.optional(),
  provider: EntityProviderSchema.optional(),
  q: z.string().optional(),
  limit: z.number().optional(),
});

// Path params for GET /entities/{id}
export const EntityIdParamsSchema = z.object({
  id: z.string(),
});

// POST /oauth-requests body
export const OAuthRequestInputSchema = z.object({
  provider: z.string(),
  returnUrl: z.string().optional(),
});

// POST /integrations/google body
export const CreateGoogleWorkspaceConfigInputSchema = z.object({
  projectIds: z.array(z.string()),
  googleDomain: z.string(),
  saKeyJson: z.record(z.string(), z.unknown()),
});

export const GoogleWorkspaceConfigParamsSchema = z.object({
  integrationId: z.string(),
});

export const GoogleWorkspaceProjectParamsSchema = z.object({
  integrationId: z.string(),
  projectId: z.string(),
});

// POST /integrations/google/configs/{integrationId}/projects body
export const AddGoogleWorkspaceProjectInputSchema = z.object({
  projectId: z.string(),
});

// POST /integrations/slack body
export const CreateSlackIntegrationConfigInputSchema = z.object({
  projectIds: z.array(z.string()),
  appName: z.string(),
  botToken: z.string(),
  clientId: z.string(),
  clientSecret: z.string(),
});

export const SlackIntegrationConfigParamsSchema = z.object({
  integrationId: z.string(),
});

export const SlackIntegrationProjectParamsSchema = z.object({
  integrationId: z.string(),
  projectId: z.string(),
});

export const SlackIntegrationChannelParamsSchema = z.object({
  integrationId: z.string(),
  channelId: z.string(),
});

export const SlackChannelActionParamsSchema = SlackIntegrationChannelParamsSchema.extend({
  actionId: z.string(),
});

// POST /integrations/slack/configs/{integrationId}/projects body
export const AddSlackIntegrationProjectInputSchema = z.object({
  projectId: z.string(),
});

// PUT /integrations/slack/configs/{integrationId}/app-name body. An empty name
// is accepted and clears the stored one; the backend refuses over 35 characters.
export const SetSlackAppNameInputSchema = z.object({
  appName: z.string(),
});

// PUT /integrations/slack/configs/{integrationId}/signing-secret body
export const SetSlackSigningSecretInputSchema = z.object({
  signingSecret: z.string(),
});

// POST /integrations/slack/configs/{integrationId}/channels body
export const BindSlackChannelInputSchema = z.object({
  slackChannelId: z.string(),
  slackChannelName: z.string(),
  projectId: z.string(),
  // Without it a channel already bound elsewhere is refused with 409; with it the
  // old binding is soft-deleted and the new one written in one transaction.
  reassign: z.boolean().optional(),
});

// all_shared ingests every page the integration token can see; selected ingests
// only selectedRootIds and their descendants.
export const NotionSyncModeSchema = z.enum(["all_shared", "selected"]);
export type NotionSyncMode = z.infer<typeof NotionSyncModeSchema>;

// A Notion database view (tab). Selecting a database root's views narrows it to
// the union of their saved filters; no views selected syncs every row.
export const NotionViewSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type NotionView = z.infer<typeof NotionViewSchema>;

// selectedRootIds scopes what the deployment syncs while syncMode is selected;
// rootViews maps a selected database root to its chosen view IDs (absent = all rows).
const NotionDeploymentSchema = z.object({
  projectId: z.string(),
  selectedRootIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  syncMode: NotionSyncModeSchema,
  rootViews: z
    .record(z.string(), z.array(z.string()))
    .nullish()
    .transform((v) => v ?? {}),
});

// The bot token is write-only: the backend never returns it, so it's absent here.
export const NotionIntegrationConfigSchema = z.object({
  id: z.string(),
  workspaceName: z.string(),
  botUserId: z.string(),
  installType: z.string(),
  enabled: z.boolean(),
  // Set when Notion rejected the stored token; syncing is paused until it's rotated.
  authBrokenAt: z.string().nullish(),
  projectIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  deployments: z
    .array(NotionDeploymentSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type NotionIntegrationConfig = z.infer<typeof NotionIntegrationConfigSchema>;

export const NotionIntegrationConfigsResponseSchema = z.object({
  integrations: z
    .array(NotionIntegrationConfigSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type NotionIntegrationConfigsResponse = z.infer<typeof NotionIntegrationConfigsResponseSchema>;

// botToken is the pasted Notion internal-integration secret.
export const CreateNotionIntegrationConfigInputSchema = z.object({
  botToken: z.string(),
  projectIds: z.array(z.string()),
});

export const NotionIntegrationConfigParamsSchema = z.object({
  integrationId: z.string(),
});

export const RotateNotionIntegrationTokenInputSchema = z.object({
  botToken: z.string(),
});

export const NotionIntegrationProjectParamsSchema = z.object({
  integrationId: z.string(),
  projectId: z.string(),
});

// An empty selectedRootIds syncs nothing.
export const AddNotionIntegrationProjectInputSchema = z.object({
  projectId: z.string(),
  selectedRootIds: z.array(z.string()),
});

// allowPrune must be true to commit a narrowing change (switching to selected,
// or removing pages); the backend deletes the now-out-of-scope pages from the
// project's search index.
export const UpdateNotionSyncModeInputSchema = z.object({
  syncMode: NotionSyncModeSchema,
  selectedRootIds: z.array(z.string()),
  allowPrune: z.boolean(),
  // Keyed by selected database root id; maps to chosen view IDs. An absent or
  // empty list syncs all of the database's rows.
  rootViews: z.record(z.string(), z.array(z.string())).optional(),
});

// parentId is absent when the node's parent is the workspace root.
export const NotionDiscoverNodeSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string(),
  url: z.string(),
  parentId: z.string().optional(),
});
export type NotionDiscoverNode = z.infer<typeof NotionDiscoverNodeSchema>;

export const NotionDiscoverResponseSchema = z.object({
  nodes: z
    .array(NotionDiscoverNodeSchema)
    .nullable()
    .transform((v) => v ?? []),
});

export const NotionViewsResponseSchema = z.object({
  views: z
    .array(NotionViewSchema)
    .nullable()
    .transform((v) => v ?? []),
});

export const NotionDatabaseViewsParamsSchema = z.object({
  integrationId: z.string(),
  databaseId: z.string(),
});

export const NotionDatabasePageSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string().optional(),
});
export type NotionDatabasePage = z.infer<typeof NotionDatabasePageSchema>;

export const NotionDatabasePagesResponseSchema = z.object({
  pages: z
    .array(NotionDatabasePageSchema)
    .nullable()
    .transform((v) => v ?? []),
});

// A project member's view of the deployed Notion integration: authBrokenAt is
// set when Notion rejected the token, absent while healthy.
export const NotionProjectIntegrationResponseSchema = z.object({
  authBrokenAt: z.string().nullish(),
});

export const NotionProjectDatabasePagesParamsSchema = z.object({
  databaseId: z.string(),
});

// all_teams ingests every team the app actor can reach; selected ingests only
// selectedTeamIds, each optionally narrowed to specific Linear projects.
export const LinearSyncModeSchema = z.enum(["all_teams", "selected"]);
export type LinearSyncMode = z.infer<typeof LinearSyncModeSchema>;

// teamProjects narrows a selected team to specific Linear projects; an absent
// entry syncs the whole team. Project is optional on a Linear issue, so
// includeUnprojected decides whether the issues carrying none are ingested.
const LinearDeploymentSchema = z.object({
  projectId: z.string(),
  syncMode: LinearSyncModeSchema,
  selectedTeamIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  teamProjects: z
    .record(z.string(), z.array(z.string()))
    .nullish()
    .transform((v) => v ?? {}),
  includeUnprojected: z.boolean(),
  lastSyncedAt: z.string().nullish(),
});

// The client secret is write-only: the backend never returns it, so it is never
// present here.
export const LinearIntegrationConfigSchema = z.object({
  id: z.string(),
  workspaceName: z.string(),
  workspaceUrlKey: z.string(),
  appUserId: z.string(),
  installType: z.string(),
  enabled: z.boolean(),
  // Set when re-minting the app actor token from the stored credentials failed;
  // syncing and tool calls stop until an admin re-saves them.
  authBrokenAt: z.string().nullish(),
  projectIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  deployments: z
    .array(LinearDeploymentSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type LinearIntegrationConfig = z.infer<typeof LinearIntegrationConfigSchema>;

export const LinearIntegrationConfigsResponseSchema = z.array(LinearIntegrationConfigSchema);

// Private teams are absent unless a workspace admin granted the application
// access on Linear's app details page.
export const LinearTeamSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  private: z.boolean(),
  projects: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .nullable()
    .transform((v) => v ?? []),
});
export type LinearTeam = z.infer<typeof LinearTeamSchema>;

export const LinearDiscoverResponseSchema = z.object({
  teams: z
    .array(LinearTeamSchema)
    .nullable()
    .transform((v) => v ?? []),
});

// url is the receiver to register in Linear; configured turns true once the
// signing secret behind it has been stored.
export const LinearWebhookInfoSchema = z.object({
  url: z.string(),
  configured: z.boolean(),
});
export type LinearWebhookInfo = z.infer<typeof LinearWebhookInfoSchema>;

// The credentials belong to a private application in the customer's own Linear
// workspace.
export const CreateLinearIntegrationConfigInputSchema = z.object({
  projectIds: z.array(z.string()),
  clientId: z.string(),
  clientSecret: z.string(),
});

export const LinearIntegrationConfigParamsSchema = z.object({
  integrationId: z.string(),
});

export const LinearIntegrationProjectParamsSchema = z.object({
  integrationId: z.string(),
  projectId: z.string(),
});

export const UpdateLinearCredentialsInputSchema = z.object({
  clientId: z.string(),
  clientSecret: z.string(),
});

// Linear shows the signing secret on the webhook's own detail page, so it
// arrives by paste rather than through a handshake. Write-only, like the client
// secret.
export const UpdateLinearWebhookSecretInputSchema = z.object({
  signingSecret: z.string(),
});

export const AddLinearIntegrationProjectInputSchema = z.object({
  projectId: z.string(),
});

// allowPrune must be true to commit a narrowing change; the backend deletes the
// now-out-of-scope issues from the project's search index.
export const UpdateLinearSyncModeInputSchema = z.object({
  syncMode: LinearSyncModeSchema,
  selectedTeamIds: z.array(z.string()),
  // Keyed by selected team id; an absent or empty entry syncs the whole team.
  teamProjects: z.record(z.string(), z.array(z.string())),
  includeUnprojected: z.boolean(),
  allowPrune: z.boolean(),
});

// truncated is true when the match count hit Notion's 10,000-row query cap.
export const NotionViewCountResponseSchema = z.object({
  count: z.number(),
  truncated: z.boolean(),
});
export type NotionViewCountResponse = z.infer<typeof NotionViewCountResponseSchema>;

export const NotionViewCountParamsSchema = z.object({
  integrationId: z.string(),
  databaseId: z.string(),
  viewId: z.string(),
});

// verificationToken is empty until Notion POSTs the handshake back; the public
// endpoint only stores it while a verification window is open. verifyOpenUntil
// is the RFC3339 instant the window closes, empty when none is open.
// recommendedApiVersion is the Notion API version to pick in the webhook config's
// version dropdown, empty when the backend has no recommendation.
export const NotionWebhookInfoSchema = z.object({
  webhookUrl: z.string(),
  verificationToken: z
    .string()
    .nullable()
    .transform((v) => v ?? ""),
  verifyOpenUntil: z
    .string()
    .nullable()
    .transform((v) => v ?? ""),
  recommendedApiVersion: z
    .string()
    .nullish()
    .transform((v) => v ?? ""),
});
export type NotionWebhookInfo = z.infer<typeof NotionWebhookInfoSchema>;

// all_repos ingests every repository the App installation reaches, including one
// added on GitHub later; selected ingests only selectedRepoIds.
export const GitHubSyncModeSchema = z.enum(["all_repos", "selected"]);
export type GitHubSyncMode = z.infer<typeof GitHubSyncModeSchema>;

// selectedRepoIds holds GitHub's numeric repository ids as text, which survive a
// rename or a transfer where an owner-and-name pair would not.
const GitHubDeploymentSchema = z.object({
  projectId: z.string(),
  syncMode: GitHubSyncModeSchema,
  selectedRepoIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  lastSyncedAt: z.string().nullish(),
});

// The private key and the webhook secret are write-only: the backend never
// returns them, so they are never present here.
export const GitHubIntegrationConfigSchema = z.object({
  id: z.string(),
  accountLogin: z.string(),
  accountType: z.enum(["organization", "user"]),
  appSlug: z.string(),
  installType: z.string(),
  enabled: z.boolean(),
  // False until the webhook secret arrives, which is connect's second phase. An
  // incomplete connection is not a broken one: it holds a working credential.
  setupComplete: z.boolean(),
  // Set when GitHub refused a freshly minted installation token; syncing and tool
  // calls stop until the credentials are re-saved.
  authBrokenAt: z.string().nullish(),
  projectIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  deployments: z
    .array(GitHubDeploymentSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type GitHubIntegrationConfig = z.infer<typeof GitHubIntegrationConfigSchema>;

export const GitHubIntegrationConfigsResponseSchema = z.array(GitHubIntegrationConfigSchema);

export const GitHubRepositorySchema = z.object({
  id: z.string(),
  name: z.string(),
  fullName: z.string(),
  private: z.boolean(),
  archived: z.boolean(),
});
export type GitHubRepository = z.infer<typeof GitHubRepositorySchema>;

export const GitHubDiscoverResponseSchema = z.object({
  repositories: z
    .array(GitHubRepositorySchema)
    .nullable()
    .transform((v) => v ?? []),
});

// url is the receiver to register on the customer's GitHub App; configured turns
// true once the webhook secret behind it has been stored.
export const GitHubWebhookInfoSchema = z.object({
  url: z.string(),
  configured: z.boolean(),
});
export type GitHubWebhookInfo = z.infer<typeof GitHubWebhookInfoSchema>;

// The credentials belong to a GitHub App the customer registered in their own
// account. accountLogin picks the installation when the App is installed on more
// than one account, and is empty otherwise. At least one project is required.
export const CreateGitHubIntegrationConfigInputSchema = z.object({
  projectIds: z.array(z.string()),
  appId: z.string(),
  privateKey: z.string(),
  accountLogin: z.string(),
});

export const GitHubIntegrationConfigParamsSchema = z.object({
  integrationId: z.string(),
});

export const GitHubIntegrationProjectParamsSchema = z.object({
  integrationId: z.string(),
  projectId: z.string(),
});

export const UpdateGitHubCredentialsInputSchema = z.object({
  appId: z.string(),
  privateKey: z.string(),
  accountLogin: z.string(),
});

// The customer sets this secret on their App's webhook, so it arrives by paste.
export const UpdateGitHubWebhookSecretInputSchema = z.object({
  secret: z.string(),
});

export const AddGitHubIntegrationProjectInputSchema = z.object({
  projectId: z.string(),
});

// allowPrune must be true to commit a narrowing change; the backend deletes the
// now-out-of-scope issues, pull requests and discussions from the project's
// search index.
export const UpdateGitHubSyncModeInputSchema = z.object({
  syncMode: GitHubSyncModeSchema,
  selectedRepoIds: z.array(z.string()),
  allowPrune: z.boolean(),
});
